import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  spaceDetailQueryOptions,
  spacesListQueryOptions,
  updateSpace,
  deleteSpace,
  removeMember,
  setMemberRole,
  invalidateSpaceScopedQueries,
} from "./spaces-queries";
import {
  type SessionVisibility,
  type AccessMode,
  type SpaceRole,
  deriveUiType,
  type SpaceUiType,
  UI_TYPE_LABEL,
} from "./types";
import { useAuth } from "@/shared/hooks/use-auth";
import { useSpaceStore } from "./space-store";
import { SpaceTypeRadios, InviteLinkRow } from "./SpaceFormParts";

/**
 * Настройки пространства — экран #2 из дизайна (бывшая «Управление доступом»).
 *
 * Owner-only: changing description/type, inviting, role changes, removal.
 * Editors get a read-only view (description + members).
 *
 * Description is auto-saved on blur — keeps the dialog in the same
 * "click and it's done" rhythm as the type radios (which fire PATCH on
 * change). No explicit «Сохранить»: less to forget, less regression
 * surface.
 *
 * Selecting a "Тип пространства" radio collapses the (visibility, access)
 * tuple into one selector — same mapping as the create dialog. We keep the
 * derived type stable: switching from "Командное общее" → "Личное"
 * changes session_visibility but not access_mode (still invite_only),
 * so it's safe even with active members.
 */

export function SpaceAccessDialog({
  spaceId,
  onClose,
}: {
  spaceId: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const currentSpaceId = useSpaceStore((s) => s.currentSpaceId);
  const setCurrentSpaceId = useSpaceStore((s) => s.setCurrentSpaceId);

  const { data: detail, isLoading, error } = useQuery(spaceDetailQueryOptions(spaceId));
  const { data: allSpaces } = useQuery(spacesListQueryOptions());

  const isOwner = detail?.role === "owner";

  // Local copy of the description for an unobtrusive edit field — initial
  // value seeded once when `detail` first arrives. We deliberately do NOT
  // re-sync on every detail change so the user's in-flight typing doesn't
  // get clobbered by a background refetch.
  const [description, setDescription] = useState<string>("");
  const [descSeeded, setDescSeeded] = useState(false);
  const [descError, setDescError] = useState<string | null>(null);
  useEffect(() => {
    if (detail && !descSeeded) {
      setDescription(detail.description);
      setDescSeeded(true);
    }
  }, [detail, descSeeded]);

  const descMutation = useMutation({
    mutationFn: (d: string) => updateSpace(spaceId, { description: d }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spaces", spaceId] });
      qc.invalidateQueries({ queryKey: ["spaces"] });
      setDescError(null);
    },
    onError: (e: Error) => setDescError(e.message),
  });

  const handleDescBlur = () => {
    if (!detail) return;
    if (description === detail.description) return;
    descMutation.mutate(description);
  };

  const typeMutation = useMutation({
    mutationFn: async (next: SpaceUiType) => {
      // Public spaces share infra but keep chats private per person —
      // `organization + private`. The combo `organization + shared` is
      // rejected by the backend invariant in `_validate_combo`.
      const visibility: SessionVisibility =
        next === "team_shared" ? "shared" : "private";
      const access: AccessMode = next === "public" ? "organization" : "invite_only";
      return updateSpace(spaceId, { session_visibility: visibility, access_mode: access });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spaces", spaceId] });
      qc.invalidateQueries({ queryKey: ["spaces"] });
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: SpaceRole }) =>
      setMemberRole(spaceId, uid, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spaces", spaceId] }),
  });

  const removeMutation = useMutation({
    mutationFn: (uid: string) => removeMember(spaceId, uid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["spaces", spaceId] });
      qc.invalidateQueries({ queryKey: ["spaces"] });
      invalidateSpaceScopedQueries(qc);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSpace(spaceId),
    onSuccess: () => {
      // If we just nuked the active space, fall back to any other one the
      // user still belongs to so the next request has a valid X-Space-Id.
      // Setting null is also fine — backend lazy-resolves the user's first
      // space — but flipping explicitly avoids a flash of cross-space data.
      if (currentSpaceId === spaceId) {
        const next = (allSpaces ?? []).find(
          (s) => s.id !== spaceId && s.role !== null,
        );
        setCurrentSpaceId(next?.id ?? null);
      }
      qc.removeQueries({ queryKey: ["spaces", spaceId] });
      qc.removeQueries({ queryKey: ["sessions", "in-space", spaceId] });
      qc.invalidateQueries({ queryKey: ["spaces"] });
      invalidateSpaceScopedQueries(qc);
      onClose();
      // The dashboard route `/spaces/<id>` would otherwise stay mounted on
      // the deleted id and try to refetch — sending the user home unmounts
      // it cleanly.
      navigate({ to: "/" });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="flex w-full max-w-xl max-h-[90vh] flex-col overflow-hidden rounded-3xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-black/5 px-6 py-4">
          <h2 className="font-display text-xl font-semibold text-black/90">Настройки пространства</h2>
          <button onClick={onClose} aria-label="Закрыть" className="rounded-full p-1 hover:bg-black/5">
            <CloseIcon />
          </button>
        </header>

        {isLoading ? (
          <div className="py-10 text-center text-sm text-black/50">Загрузка…</div>
        ) : error || !detail ? (
          <div className="py-10 text-center text-sm text-red-600">
            {error instanceof Error ? error.message : "Не удалось загрузить пространство"}
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="mb-2 text-sm text-black/70 font-medium">Описание</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={handleDescBlur}
              rows={2}
              readOnly={!isOwner}
              placeholder={isOwner ? "Что за проект, для чего нужно" : "Описание не задано"}
              className="mb-1 w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm focus:outline-none focus:border-black/30 read-only:bg-black/[0.02] read-only:cursor-default"
            />
            {descError && <div className="mb-3 text-xs text-red-600">{descError}</div>}
            {!descError && <div className="mb-5" />}

            <div className="mb-2 text-sm text-black/70 font-medium">Тип пространства</div>
            <div className="mb-6">
              <SpaceTypeRadios
                value={deriveUiType(detail)}
                onChange={(v) => typeMutation.mutate(v)}
                isAdmin={isAdmin}
                disabled={!isOwner || typeMutation.isPending}
              />
            </div>

            {isOwner && detail.access_mode !== "organization" && (
              <>
                <div className="mb-2 text-sm text-black/70 font-medium">Ссылка для приглашения</div>
                <div className="mb-6">
                  <InviteLinkRow spaceId={spaceId} />
                </div>
              </>
            )}

            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm text-black/70 font-medium">
                Участники <span className="text-black/40">({detail.members.length})</span>
              </div>
            </div>
            <ul className="flex flex-col gap-2">
              {detail.members.map((m) => (
                <li key={m.user_id} className="flex items-center gap-3 rounded-xl px-1 py-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black/10 text-sm font-medium text-black/70">
                    {(m.name || m.email || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-black/90">{m.name || m.email}</div>
                    <div className="truncate text-xs text-black/45">{m.email}</div>
                  </div>
                  <RoleControl
                    role={m.role}
                    canEdit={isOwner && m.user_id !== user?.id && m.role !== "owner"}
                    onChange={(role) => roleMutation.mutate({ uid: m.user_id, role })}
                  />
                  {isOwner && m.user_id !== user?.id && m.role !== "owner" && (
                    <button
                      type="button"
                      onClick={() => removeMutation.mutate(m.user_id)}
                      className="rounded-full p-2 text-black/40 hover:bg-black/5 hover:text-red-600"
                      aria-label="Удалить"
                    >
                      <TrashIcon />
                    </button>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-2 text-xs text-black/40">
              Тип «{UI_TYPE_LABEL[deriveUiType(detail)]}». Сессии участников
              {detail.session_visibility === "shared" ? " видны всем." : " приватны."}
            </div>
            </div>

            <div className="shrink-0 border-t border-black/5 px-6 py-4">
              <div className="flex items-center justify-between gap-3">
                {isOwner ? (
                  <button
                    type="button"
                    onClick={() => {
                      const ok = window.confirm(
                        `Удалить пространство «${detail.name}»?\n\n` +
                          "Будут безвозвратно удалены все сессии, агенты, навыки, " +
                          "коннекторы и автоматизации этого пространства. Это действие нельзя отменить.",
                      );
                      if (ok) deleteMutation.mutate();
                    }}
                    disabled={deleteMutation.isPending}
                    className="rounded-full px-4 py-2 text-sm text-red-600 hover:bg-red-05 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deleteMutation.isPending ? "Удаление…" : "Удалить пространство"}
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full bg-black px-5 py-2 text-sm text-white hover:bg-black/85"
                >
                  Готово
                </button>
              </div>
              {deleteMutation.error && (
                <div className="mt-2 text-right text-xs text-red-600">
                  {(deleteMutation.error as Error).message}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RoleControl({
  role,
  canEdit,
  onChange,
}: {
  role: SpaceRole;
  canEdit: boolean;
  onChange: (r: SpaceRole) => void;
}) {
  const label = role === "owner" ? "Владелец" : "Редактор";
  if (!canEdit) {
    return <span className="text-xs text-black/50">{label}</span>;
  }
  return (
    <select
      value={role}
      onChange={(e) => onChange(e.target.value as SpaceRole)}
      className="rounded-md bg-transparent px-2 py-1 text-xs text-black/70 focus:outline-none hover:bg-black/5"
    >
      <option value="editor">Редактор</option>
    </select>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}
